import React, {
  useState, useCallback, useMemo,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { EMPTY_TARGETS } from '../../components/ui/AssignmentTargetPicker';
import RaffleQuestionsEditor from './components/RaffleQuestionsEditor';
import useRaffleApi, { toRafflePayload } from './config';

// Çekilişler (RF) — İK yönetim listesi. UI altın kuralları: başlık YALNIZ DataTable'da,
// "Yeni" butonu tablonun İÇİNDE, oluşturma/düzenleme GenericFormDrawer ile (modal DEĞİL).
const Container = styled.div``;

export default function RafflesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();
  const canManage = canWrite('RF');

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [questions, setQuestions] = useState([]);
  const [confirm, setConfirm] = useState({ isOpen: false, kind: null, row: null });
  const [busy, setBusy] = useState(false);

  const {
    fetching, data, pagination, columns: baseColumns, objectSchema,
    create, update, remove, publish, cancel,
  } = useRaffleApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const openNew = useCallback(() => {
    setQuestions([]);
    setDrawer({ open: true, row: null });
  }, []);

  const openEdit = useCallback((row) => {
    setQuestions((row.questions || []).map((q) => ({
      id: q.id, text: q.text, isRequired: q.isRequired,
    })));
    setDrawer({ open: true, row });
  }, []);

  // Drawer'a verilen ilk değerler: kapsam sunucudan düz alanlarla gelmediği için
  // (atama satırları) düzenlemede picker BOŞ başlar — İK kapsamı yeniden seçer.
  // Bu bilinçli: yarım hidratlanmış bir kapsam, sessizce yanlış kitleye yayın yapardı.
  const initialValues = useMemo(() => {
    const row = drawer.row;
    if (!row) {
      return {
        winnerCount: 1,
        itemsPerWinner: 1,
        backupWinnerCount: 0,
        winnersVisibility: 'winners_only',
        targets: { ...EMPTY_TARGETS, excludes: { ...EMPTY_TARGETS.excludes } },
      };
    }
    return {
      ...row,
      entryDeadline: row.entryDeadline ? row.entryDeadline.split('T')[0] : '',
      targets: { ...EMPTY_TARGETS, excludes: { ...EMPTY_TARGETS.excludes } },
    };
  }, [drawer.row]);

  const handleSave = useCallback(async (formData) => {
    const payload = toRafflePayload(formData, questions);
    const ok = drawer.row ? await update(drawer.row.id, payload) : await create(payload);
    return ok;
  }, [drawer.row, questions, create, update]);

  const askConfirm = useCallback((kind, row) => setConfirm({ isOpen: true, kind, row }), []);

  const handleConfirm = useCallback(async () => {
    const { kind, row } = confirm;
    setBusy(true);
    if (kind === 'publish') await publish(row.id);
    else if (kind === 'cancel') await cancel(row.id);
    else if (kind === 'delete') await remove([row.id]);
    setBusy(false);
    setConfirm({ isOpen: false, kind: null, row: null });
  }, [confirm, publish, cancel, remove]);

  const buildActions = useCallback((row) => {
    const actions = [{
      type: 'details',
      label: t('raffle.actions.detail'),
      onClick: () => history.push(`/raffles/${row.id}`),
    }];
    if (!canManage) return actions;

    if (row.status === 'draft') {
      actions.push({ type: 'edit', label: t('common.edit'), onClick: () => openEdit(row) });
      actions.push({ type: 'publish', label: t('raffle.actions.publish'), onClick: () => askConfirm('publish', row) });
      actions.push({ type: 'delete', label: t('common.delete'), onClick: () => askConfirm('delete', row) });
    }
    if (row.status === 'published') {
      actions.push({ type: 'edit', label: t('common.edit'), onClick: () => openEdit(row) });
      actions.push({ type: 'cancel', label: t('raffle.actions.cancel'), onClick: () => askConfirm('cancel', row) });
    }
    return actions;
  }, [t, canManage, history, openEdit, askConfirm]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return <OptionsMenu actions={buildActions(row)} row={row} />;
        },
      },
    },
  ], [baseColumns, data, buildActions]);

  const confirmTexts = useMemo(() => {
    const map = {
      publish: ['raffle.confirm.publishTitle', 'raffle.confirm.publishMessage'],
      cancel: ['raffle.confirm.cancelTitle', 'raffle.confirm.cancelMessage'],
      delete: ['raffle.confirm.deleteTitle', 'raffle.confirm.deleteMessage'],
    };
    const [titleKey, messageKey] = map[confirm.kind] || ['', ''];
    return {
      title: titleKey ? t(titleKey) : '',
      message: messageKey ? t(messageKey, { title: confirm.row?.title || '' }) : '',
    };
  }, [confirm, t]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, row: null })}
        title={drawer.row ? t('raffle.edit') : t('raffle.new')}
        schema={objectSchema}
        initialValues={drawer.row ? initialValues : undefined}
        onSave={handleSave}
      >
        <RaffleQuestionsEditor questions={questions} onChange={setQuestions} />
      </GenericFormDrawer>

      <ConfirmDialog
        isOpen={confirm.isOpen}
        title={confirmTexts.title}
        message={confirmTexts.message}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm({ isOpen: false, kind: null, row: null })}
        loading={busy}
        type={confirm.kind === 'publish' ? 'info' : 'danger'}
      />

      <DataTable
        title={t('raffle.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
        canCreateNewRecord={canManage}
        newButtonLabel={t('raffle.new')}
        onNewButtonClick={openNew}
      />
    </Container>
  );
}
